<script setup lang="ts">
import type { Components } from '@/api/generated/api';
import { useMobile } from '@/composables/useMobile';
import PerformanceLevelNotSetBadge from '@/core/badges/PerformanceLevelNotSetBadge.vue';
import PerformanceLevelRatingOptions from '@/core/performance-visuals/PerformanceLevelRatingOptions.vue';
import PerformanceLevelRatingPopover from '@/core/performance-visuals/PerformanceLevelRatingPopover.vue';
import type { TPerformanceLevelRatingMode } from '@/core/performance-visuals/performanceLevelRating.types';
import { computed } from 'vue';

defineOptions({
  inheritAttrs: false,
});

const emit = defineEmits<{
  'update:modelValue': [value: number | null];
}>();

const props = withDefaults(
  defineProps<{
    accessibleLabel: string;
    disabled?: boolean;
    mode?: TPerformanceLevelRatingMode;
    modelValue: number | null;
    performanceVisual?: Components.Schemas.PerformanceVisualDto | null;
  }>(),
  {
    disabled: false,
    mode: 'responsive',
    performanceVisual: undefined,
  },
);

const { isMobile } = useMobile();

const hasPerformanceLevels = computed<boolean>(() => !!props.performanceVisual?.performanceLevelsV2.length);
const usePopover = computed<boolean>(() => props.mode === 'responsive' && isMobile.value);
const inlineAppearance = computed<'badges' | 'cards'>(() => (props.mode === 'inline-cards' ? 'cards' : 'badges'));
</script>

<template>
  <PerformanceLevelNotSetBadge
    v-if="!hasPerformanceLevels"
    v-bind="$attrs"
    class="size-icon-xl opacity-40"
    role="img"
    :aria-label="accessibleLabel"
  />
  <PerformanceLevelRatingPopover
    v-else-if="usePopover"
    v-bind="$attrs"
    :accessible-label="accessibleLabel"
    :disabled="disabled"
    :model-value="modelValue"
    :performance-visual="performanceVisual"
    @update:model-value="emit('update:modelValue', $event)"
  />
  <PerformanceLevelRatingOptions
    v-else
    v-bind="$attrs"
    :appearance="inlineAppearance"
    :accessible-label="accessibleLabel"
    :disabled="disabled"
    :model-value="modelValue"
    :performance-visual="performanceVisual"
    @update:model-value="emit('update:modelValue', $event)"
  />
</template>
